/**
 * Creates and triggers a file input with proper DOM attachment to respect browser user activation constraints.
 *
 * When file inputs are created programmatically and clicked immediately within portalized menus,
 * browsers (especially Safari/WebKit) may block the click due to lost user activation.
 * This utility ensures the input is properly attached to the DOM and clicked in the next frame.
 *
 * @param options - Configuration for the file input
 * @param onChange - Callback when files are selected
 */
export function triggerFileInput(
  options: {
    multiple?: boolean
    accept?: string
    webkitdirectory?: boolean
  },
  onChange: (files: FileList) => void
): void {
  const input = document.createElement('input')
  input.type = 'file'
  input.style.display = 'none'

  if (options.multiple) {
    input.multiple = true
  }

  if (options.accept) {
    input.accept = options.accept
  }

  if (options.webkitdirectory) {
    input.webkitdirectory = true
    input.multiple = true // Required for webkitdirectory to work properly
  }

  const handleChange = (event: Event) => {
    const files = (event.target as HTMLInputElement).files
    if (files) {
      onChange(files)
    }
    input.removeEventListener('change', handleChange)
    input.remove()
  }

  input.addEventListener('change', handleChange)

  // Attach to DOM to ensure user activation is preserved
  document.body.append(input)

  // Defer click to next frame to work around portal/menu lifecycle timing
  requestAnimationFrame(() => {
    input.click()
  })
}
